import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import {
  Card, Container, Button, Modal, ModalHeader, ModalBody, ModalFooter, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Row, Col
} from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useTranslation } from "react-i18next";

const localizer = momentLocalizer(moment);

const CustomEvent = ({ event, title }) => {
  return (
    <span>
      <strong>{title}</strong>
    </span>
  );
};

// Define GraphQL Queries and Mutations
const APPOINTMENTS_QUERY = gql`
  query MyQuery($id: Int = 1) {
    appointments(where: {staff_to_service_relationship: {service: {service_provider_id: {_eq: $id}}}}) {
      customer_user_id
      end_time
      id
      start_time
      status
      staff_to_service_relationship {
        service {
          name
          color
        }
        staff {
          name
        }
      }
      user {
        first_name
        last_name
        gender
        email
        phone_number
        address_city
      }
    }
    service_providers_by_pk(id: $id) {
      company_name
      opening_hours
    }
    staff(where: {service_provider_id: {_eq: $id}}) {
      name
    }
  }
`;


const UPDATE_APPOINTMENT_STATUS = gql`
  mutation MyMutation($id: Int!, $status: String!) {
    update_appointments_by_pk(pk_columns: {id: $id}, _set: {status: $status}) {
      id
    }
  }
`;

const formats = {
  dayHeaderFormat: ({ start }) => moment(start).format('MMMM DD, YYYY'),
  eventTimeRangeFormat: () => '', // Suppress time range in day/week views
  agendaTimeRangeFormat: ({ start, end }) => `${moment(start).format('LT')} - ${moment(end).format('LT')}`, // Proper time format for agenda view
};

const ProviderCalendar = () => {
  const serviceProviderId = parseInt(localStorage.getItem('opID'), 10);
  const { t } = useTranslation();

  // State for modal and view
  const [modal, setModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [view, setView] = useState('week'); 
  const [date, setDate] = useState(new Date()); // Store the current date (default today)

  const [appointments, setAppointments] = useState([]); // Store appointments

  // Dropdown states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('ALL STATUS');
  const [selectedStaff, setSelectedStaff] = useState('ALL STAFF');

  const [confirmModal, setConfirmModal] = useState(false);
const [actionToConfirm, setActionToConfirm] = useState(null);
const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
const calendarRef = useRef(null);

useEffect(() => {
  // Check if there's a selected appointment in localStorage
  const selectedAppointmentLocal = localStorage.getItem('selectedAppointment');
  console.log("appointment",selectedAppointmentLocal)

  if (selectedAppointmentLocal) {
    const appointment = JSON.parse(selectedAppointmentLocal);
    // Navigate to the day of the appointment
    navigateToAppointment(appointment);
    // Remove the appointment from localStorage after using it
    localStorage.removeItem('selectedAppointment');
  }
}, []);

const navigateToAppointment = (appointment) => {
  // Set the view to 'day' and navigate to the appointment's start date
  setView('day');
  setDate(new Date(appointment.start_time)); // Set the calendar's current date to the appointment's start date
  // Optional: Scroll to the specific time of the appointment
  setTimeout(() => {
    if (calendarRef.current) {
      calendarRef.current.scrollToTime(appointment.start); // Scroll to the specific time of the appointment
    }
  }, 0);
};

  const toggleStatusDropdown = () => setStatusDropdownOpen(prevState => !prevState);
  const toggleStaffDropdown = () => setStaffDropdownOpen(prevState => !prevState);

  // Toggle modal
  const toggle = () => setModal(!modal);

  // Fetch appointments and opening hours
  const { data, loading, error,refetch } = useQuery(APPOINTMENTS_QUERY, {
    variables: { id: serviceProviderId },
    pollInterval: 10000, // Polling every 10 seconds
    onCompleted: (data) => {
      // Format appointments and store in state
      const formattedAppointments = data.appointments.map((appointment) => ({
        id: appointment.id,
        start: new Date(appointment.start_time),
        end: new Date(appointment.end_time),
        title: appointment.staff_to_service_relationship.service.name,
        staffName: appointment.staff_to_service_relationship.staff.name,
        status: appointment.status,
        color: appointment.staff_to_service_relationship.service.color,
      }));
      setAppointments(formattedAppointments);
    },
  });

  // Define mutation for updating the appointment status
  const [updateAppointmentStatus] = useMutation(UPDATE_APPOINTMENT_STATUS, {
    onCompleted: (data) => {
      refetch(); 
      console.log("Appointment status updated successfully:", data);
      // alert("Appointment status updated successfully!");
    },
    onError: (error) => {
      console.error("Error updating appointment status:", error);
      alert("Error updating appointment status: " + error.message);
    }
  });
  
  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading appointments.</p>;

  // Handle appointment click to open modal
  const handleSelectEvent = (appointment) => {
    const selected = {
      ...appointment,
      user: data.appointments.find((a) => a.id === appointment.id)?.user || {},
    };
    setSelectedAppointment(selected);
    toggle();
  };

  // Filter appointments by status and staff
  const filterAppointments = (appointments) => {
    return appointments.filter((appointment) => {
      const matchesStatus = selectedStatus === 'ALL STATUS' || appointment.status === selectedStatus;
      const matchesStaff = selectedStaff === 'ALL STAFF' || appointment.staffName === selectedStaff;

      return matchesStatus && matchesStaff;
    });
  };

  // Custom event display logic depending on view type
  const eventRenderer = (event, view) => {
    if (view === 'agenda') {
      return `${event.title} (${event.staffName})`;
    }
    const startTime = moment(event.start).format('LT'); // Get only start time
    return `${event.title} (${event.staffName}) - ${startTime}`;
  };

  const eventPropGetter = (event) => {
    let backgroundColor;
    if (event.status === "CANCELLED") {
      backgroundColor = "gray";
    } else if (event.status === "PENDING") {
      backgroundColor = event.color || "#3174ad";
    } else {
      backgroundColor = event.color || "#3174ad";
    }
    const isPast = new Date(event.end) < new Date();
    
    // Apply pulsating effect for pending status
    const className = event.status === "PENDING" && !isPast? "pending-event" : "";
    
    return {
      className, // Add the pulsating class
      style: {
        backgroundColor: isPast ? "lightgray" : backgroundColor,
        opacity: isPast ? 0.5 : 1,
      },
    };
  };
  

  const earliestOpeningTime = Object.values(data.service_providers_by_pk.opening_hours || {}).flat()
    .map(period => moment(period.start, "HH:mm"))
    .sort((a, b) => a.diff(b))[0];
  const minTime = earliestOpeningTime ? earliestOpeningTime.toDate() : new Date(1970, 1, 1, 9, 0, 0);

  const openConfirmModal = (appointmentId, action) => {
    console.log("appointmentId",appointmentId)
    console.log("action",action)

    setSelectedAppointmentId(appointmentId);
    setActionToConfirm(action);
    setConfirmModal(true); // Open the modal
  };
  
  const handleConfirmAction = () => {
    if (actionToConfirm === "COMPLETED") {
      updateAppointmentStatus({
        variables: { id: selectedAppointmentId, status: "COMPLETED" },
      });
    } else if (actionToConfirm === "CANCELLED") {
      updateAppointmentStatus({
        variables: { id: selectedAppointmentId, status: "CANCELLED" },
      });
    } else if (actionToConfirm === "CONFIRMED") {
      console.log("sdsdsdf")
      updateAppointmentStatus({
        variables: { id: selectedAppointmentId, status: "CONFIRMED" },
      });
    }
  console.log("actionToConfirm",actionToConfirm)
    setConfirmModal(false); // Close the modal after action
    toggle()
  };
  

  // Function to mark an appointment as completed
  const handleMarkCompleted = async () => {
    if (selectedAppointment) {
      try {
        await updateAppointmentStatus({ variables: { id: selectedAppointment.id, status: 'COMPLETED' } });
        const updatedAppointments = appointments.map(app =>
          app.id === selectedAppointment.id ? { ...app, status: 'COMPLETED' } : app
        );
        setAppointments(updatedAppointments);
        toggle();
      } catch (error) {
        console.error("Error marking as completed:", error);
      }
    }
  };

  // Function to cancel an appointment
  const handleCancelAppointment = async () => {
    if (selectedAppointment) {
      try {
        await updateAppointmentStatus({ variables: { id: selectedAppointment.id, status: 'CANCELLED' } });
        const updatedAppointments = appointments.map(app =>
          app.id === selectedAppointment.id ? { ...app, status: 'CANCELLED' } : app
        );
        setAppointments(updatedAppointments);
        toggle();
      } catch (error) {
        console.error("Error cancelling appointment:", error);
      }
    }
  };

  // Function to confirm a pending appointment
  const handleConfirmAppointment = async () => {
    if (selectedAppointment) {
      try {
        await updateAppointmentStatus({ variables: { id: selectedAppointment.id, status: 'CONFIRMED' } });
        const updatedAppointments = appointments.map(app =>
          app.id === selectedAppointment.id ? { ...app, status: 'CONFIRMED' } : app
        );
        setAppointments(updatedAppointments);
        toggle();
      } catch (error) {
        console.error("Error confirming appointment:", error);
      }
    }
  };

  // Function to revert status to confirmed
  const handleRevertToConfirmed = async () => {
    if (selectedAppointment) {
      try {
        await updateAppointmentStatus({ variables: { id: selectedAppointment.id, status: 'CONFIRMED' } });
        const updatedAppointments = appointments.map(app =>
          app.id === selectedAppointment.id ? { ...app, status: 'CONFIRMED' } : app
        );
        setAppointments(updatedAppointments);
        toggle();
      } catch (error) {
        console.error("Error reverting appointment to confirmed:", error);
      }
    }
  };

  const filteredAppointments = filterAppointments(appointments);


  
  // Use a reference to the calendar component

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Calendar")} pageTitle={t("Home")} />

          {/* Align dropdowns next to each other */}
          <Row className="mb-3 d-flex align-items-center">
            <Col md="auto">
              <Dropdown isOpen={statusDropdownOpen} toggle={toggleStatusDropdown}>
                <DropdownToggle color="primary" caret>
                  {selectedStatus}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => setSelectedStatus('ALL STATUS')}>ALL STATUS</DropdownItem>
                  <DropdownItem onClick={() => setSelectedStatus('PENDING')}>PENDING</DropdownItem>
                  <DropdownItem onClick={() => setSelectedStatus('CONFIRMED')}>CONFIRMED</DropdownItem>
                  <DropdownItem onClick={() => setSelectedStatus('COMPLETED')}>COMPLETED</DropdownItem>
                  <DropdownItem onClick={() => setSelectedStatus('CANCELLED')}>CANCELLED</DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </Col>

            <Col md="auto">
              <Dropdown isOpen={staffDropdownOpen} toggle={toggleStaffDropdown}>
                <DropdownToggle color="secondary" caret>
                  {selectedStaff}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem onClick={() => setSelectedStaff('ALL STAFF')}>ALL STAFF</DropdownItem>
                  {data.staff.map(staff => (
                    <DropdownItem key={staff.name} onClick={() => setSelectedStaff(staff.name)}>
                      {staff.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </Col>
          </Row>

          <Card>
            <Calendar
              ref={calendarRef}  // Add this to get a reference to the calendar
              localizer={localizer}
              events={filteredAppointments}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 500 }}
              eventPropGetter={eventPropGetter}
              onSelectEvent={handleSelectEvent}
              formats={formats}
              components={{
                event: ({ event, title }) => CustomEvent({ event, title: eventRenderer(event, view) }),
              }}
              onView={(view) => setView(view)} // Capture view changes
              view={view}
              date={date}       // Pass the appointment date here
              onNavigate={(newDate) => setDate(newDate)} // Handle date changes when navigating
              min={minTime}
            />
          </Card>

          {selectedAppointment && (
  <Modal isOpen={modal} toggle={toggle}>
    <ModalHeader toggle={toggle}>Appointment Details</ModalHeader>
    <ModalBody>
      <p><strong>Service:</strong> {selectedAppointment.title}</p>
      <p><strong>Time:</strong> <strong>{moment(selectedAppointment.start).format('LT')}</strong> - <strong>{moment(selectedAppointment.end).format('LT')}</strong></p>
      <p><strong>Status:</strong> {selectedAppointment.status}</p>
      <p><strong>Staff:</strong> {selectedAppointment.staffName}</p>

  {/* Add user details here */}
  {selectedAppointment.user && (
    <>
      <p><strong>Customer Name:</strong> {selectedAppointment.user.first_name} {selectedAppointment.user.last_name}</p>
      <p><strong>Email:</strong> {selectedAppointment.user.email}</p>
      <p><strong>Phone:</strong> {selectedAppointment.user.phone_number}</p>
      <p><strong>City:</strong> {selectedAppointment.user.address_city}</p>
    </>
  )}
      {/* Action buttons moved inside ModalBody */}
      <div className="action-buttons" style={{ marginTop: '20px' }}>
        {selectedAppointment.status === 'PENDING' && (
          <>
            <Button
              color="success"
              onClick={() => openConfirmModal(selectedAppointment.id,'CONFIRMED')}
              className="mb-2"
              block
            >
              Confirm
            </Button>
            <Button
              color="danger"
              onClick={() => openConfirmModal(selectedAppointment.id,'CANCELLED')}
              className="mb-2"
              block
            >
              Cancel
            </Button>
          </>
        )}
        {selectedAppointment.status === 'CONFIRMED' && (
          <>
            <Button
              color="success"
              onClick={() => openConfirmModal(selectedAppointment.id,'COMPLETED')}
              className="mb-2"
              block
            >
              Mark Completed
            </Button>
            <Button
              color="danger"
              onClick={() => openConfirmModal(selectedAppointment.id,'CANCELLED')}
              className="mb-2"
              block
            >
              Cancel
            </Button>
          </>
        )}
        {selectedAppointment.status === 'COMPLETED' && (
          <Button
            color="primary"
            onClick={() => openConfirmModal(selectedAppointment.id,'CONFIRMED')}
            className="mb-2"
            block
          >
            Revert to Confirmed
          </Button>
        )}
        {selectedAppointment.status === 'CANCELLED' && (
          <Button
            color="primary"
            onClick={() => openConfirmModal(selectedAppointment.id,'CONFIRMED')}
            className="mb-2"
            block
          >
            Revert to Confirmed
          </Button>
        )}
      </div>
    </ModalBody>
    <ModalFooter>
      <Button color="secondary" onClick={toggle}>Close</Button>
    </ModalFooter>
  </Modal>
)}


          <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)}>
  <ModalHeader toggle={() => setConfirmModal(false)}>Confirm Action</ModalHeader>
  <ModalBody>
    Are you sure you want to {actionToConfirm === "COMPLETED" ? "mark as completed" : actionToConfirm === "CANCELLED" ? "cancel" :selectedAppointment?.status==="PENDING"?"confirm": "revert to confirmed"} this appointment?
  </ModalBody>
  <ModalFooter>
    <Button color="primary" onClick={handleConfirmAction}>Yes</Button>{' '}
    <Button color="secondary" onClick={() => setConfirmModal(false)}>No</Button>
  </ModalFooter>
</Modal>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProviderCalendar;
